/**
 * Direct-to-GCS upload transport. Two modes, chosen by the backend per file:
 *
 *   • "simple"    — one PUT of the whole File (the legacy path; fine for small
 *                   files, fast, needs no special bucket config).
 *   • "resumable" — a GCS *resumable* session: POST to open a session, then PUT
 *                   the file in 256-KiB-aligned chunks with Content-Range,
 *                   retrying a failed chunk by re-querying the committed offset
 *                   and resuming. Survives connection drops / tab sleeps and the
 *                   session URI lives ~1 week, so it isn't racing the signed-URL
 *                   expiry the way a single multi-GB PUT is.
 *
 * `uploadBytes(meta, file, opts)` is the single entry point callers use — it
 * dispatches on `meta.uploadMode`. `onProgress(loaded, total)` reports RAW byte
 * counts (NOT a percentage), matching the existing FileService/ImportService
 * contract so speed/ETA aggregation is unchanged. `signal` (AbortSignal) cancels
 * an in-flight upload (between or within chunks).
 *
 * NOTE: the resumable path needs the bucket CORS policy to allow POST and to
 * EXPOSE the `Location` response header — otherwise the browser can't read the
 * session URI. See cors.json + the "Resumable uploads" note in ZIP_LIMIT_PLAN.md.
 * The backend only ever returns uploadMode:"resumable" when that's been
 * configured (RESUMABLE_UPLOADS_ENABLED), so the simple path is the default.
 */

import axios from "axios";

/** GCS requires every NON-final resumable chunk to be a multiple of 256 KiB. */
export const GCS_CHUNK_MULTIPLE = 256 * 1024;
/** Default chunk size: 8 MiB (= 32 × 256 KiB). Must stay a 256-KiB multiple. */
export const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024;
/** Per-chunk retry budget before the whole upload is failed. */
export const MAX_CHUNK_ATTEMPTS = 5;

/**
 * Format a resumable-chunk `Content-Range` header value.
 *  - a real chunk → "bytes <start>-<end-1>/<total>" (HTTP ranges are inclusive)
 *  - an empty status query (end<=start) → "bytes * /<total>"
 */
export function contentRange(start, end, total) {
    if (end <= start) return `bytes */${total}`;
    return `bytes ${start}-${end - 1}/${total}`;
}

/**
 * Parse the next byte offset to send from a 308 response's `Range` header.
 * GCS replies "Range: bytes=0-<lastCommitted>", so the next offset is
 * lastCommitted + 1. A missing/blank header means nothing is committed yet → 0.
 */
export function nextOffsetFromRange(rangeHeader) {
    if (!rangeHeader) return 0;
    const m = /bytes=0-(\d+)/.exec(String(rangeHeader));
    return m ? Number(m[1]) + 1 : 0;
}

/** Read the `Location`/`Range` header case-insensitively (axios lower-cases, but be safe). */
function header(res, name) {
    const h = res && res.headers;
    if (!h) return undefined;
    return h[name] ?? h[name.toLowerCase()] ?? h[name.toUpperCase()];
}

/** Open a resumable session and return its session URI. */
async function initiateSession(initUrl, contentType, signal) {
    const res = await axios.post(initUrl, "", {
        headers: { "x-goog-resumable": "start", "Content-Type": contentType },
        signal,
    });
    const uri = header(res, "location");
    if (!uri) {
        throw new Error(
            "Resumable upload could not start: GCS returned no session URI. This usually " +
                "means the bucket CORS policy doesn't expose the 'Location' response header."
        );
    }
    return uri;
}

/**
 * Ask GCS how many bytes it has actually committed for a session (used to resume
 * after a failed chunk). Returns the next offset to send, or `total` if the
 * object is already fully received.
 */
export async function queryCommittedOffset(sessionUri, total, signal) {
    const res = await axios.put(sessionUri, "", {
        headers: { "Content-Range": contentRange(0, 0, total) },
        signal,
        validateStatus: (s) => s === 200 || s === 201 || s === 308,
    });
    if (res.status === 200 || res.status === 201) return total; // already finalized
    const range = header(res, "range");
    if (!range) {
        // 308 with no Range = committed offset unknown. Do NOT assume 0 — that
        // would re-upload the whole file from the start. Surface it so the caller
        // keeps its current offset (resends only the in-flight chunk) instead.
        throw new Error("Resumable status query returned no Range header (committed offset unknown).");
    }
    return nextOffsetFromRange(range);
}

/**
 * Status-query the session purely to learn whether GCS has FINALIZED the object
 * (committed all bytes). Returns true only on a 200/201 — a 308, even one whose
 * Range covers every byte, is NOT proof the object was committed.
 */
async function isFinalized(sessionUri, total, signal) {
    const res = await axios.put(sessionUri, "", {
        headers: { "Content-Range": contentRange(0, 0, total) },
        signal,
        validateStatus: (s) => s === 200 || s === 201 || s === 308,
    });
    return res.status === 200 || res.status === 201;
}

/**
 * Upload `file` to GCS via a resumable session opened at `initUrl`.
 * `opts`: { contentType?, onProgress?(loaded,total), signal?, chunkSize? }.
 */
export async function resumableUpload(initUrl, file, opts = {}) {
    const { contentType, onProgress, signal, chunkSize = DEFAULT_CHUNK_SIZE } = opts;
    // GCS rejects a non-final chunk whose length isn't a 256-KiB multiple, which
    // would make the resume loop spin forever — fail loudly and early instead.
    if (chunkSize % GCS_CHUNK_MULTIPLE !== 0) {
        throw new Error(`resumable chunkSize must be a multiple of ${GCS_CHUNK_MULTIPLE} bytes (got ${chunkSize}).`);
    }
    const total = file.size;
    const type = contentType || file.type || "application/octet-stream";

    const sessionUri = await initiateSession(initUrl, type, signal);

    // Monotonic progress: never report a `loaded` below what we've already
    // reported (a chunk retry restarts its byte counter from the chunk start).
    let reported = 0;
    const report = (loaded) => {
        if (!onProgress) return;
        if (loaded > reported) reported = loaded;
        onProgress(reported, total);
    };

    let offset = 0;
    let attempts = 0;
    // Forward-progress ceiling: even if a server commits a tiny prefix per PUT
    // (so MAX_CHUNK_ATTEMPTS never trips), bound the total round-trips.
    let iterations = 0;
    const maxIterations = (Math.ceil(total / chunkSize) || 1) * 4 + MAX_CHUNK_ATTEMPTS;

    while (offset < total) {
        if (++iterations > maxIterations) {
            throw new Error("Resumable upload made too little forward progress; aborting.");
        }
        // Per-iteration const so the progress/fallback closures below capture THIS
        // chunk's start, not the loop-mutated `offset` (keeps eslint no-loop-func
        // happy and is what we actually mean).
        const chunkStart = offset;
        const end = Math.min(chunkStart + chunkSize, total);
        const blob = file.slice(chunkStart, end);
        try {
            // eslint-disable-next-line no-await-in-loop -- chunks are strictly sequential
            const res = await axios.put(sessionUri, blob, {
                headers: { "Content-Range": contentRange(chunkStart, end, total) },
                signal,
                // 308 ("Resume Incomplete") is the normal "send more" reply; axios
                // would otherwise treat it as an error.
                validateStatus: (s) => s === 200 || s === 201 || s === 308,
                onUploadProgress: (e) => report(chunkStart + (e.loaded || 0)),
            });
            attempts = 0; // a chunk landed — reset the retry budget
            if (res.status === 200 || res.status === 201) {
                report(total);
                return; // object finalized
            }
            // Trust the server's committed offset over our own arithmetic in case a
            // chunk was only partially received; never go backwards.
            const committed = nextOffsetFromRange(header(res, "range"));
            offset = committed > chunkStart ? committed : end;
            report(offset);
        } catch (err) {
            if (signal && signal.aborted) throw err; // cancellation isn't retryable
            attempts += 1;
            if (attempts >= MAX_CHUNK_ATTEMPTS) throw err;
            // The failed PUT may have partially landed — re-sync before retrying so
            // we resume from exactly what GCS has, not a guess.
            // eslint-disable-next-line no-await-in-loop
            offset = await queryCommittedOffset(sessionUri, total, signal).catch(() => chunkStart);
        }
    }

    // All bytes have been SENT, but we only return success above on a finalizing
    // 2xx. We can reach here when the final chunk answered 308 with a full Range
    // (bytes received, object not yet committed) — confirm finalization with a
    // status query before reporting complete, rather than trusting the 308.
    for (let i = 0; i < MAX_CHUNK_ATTEMPTS; i++) {
        // eslint-disable-next-line no-await-in-loop
        if (await isFinalized(sessionUri, total, signal)) {
            report(total);
            return;
        }
    }
    throw new Error("Resumable upload sent all bytes but GCS never confirmed finalization.");
}

/**
 * Upload `file` to GCS using whichever transport the backend chose for it.
 * `meta` = { signedUrl, uploadMode } from an init / batch-init / version-init
 * response. Returns a promise that resolves when the bytes are fully stored.
 */
export function uploadBytes(meta, file, opts = {}) {
    const { onProgress, signal } = opts;
    const contentType = file.type || "application/octet-stream";

    if (meta && meta.uploadMode === "resumable") {
        return resumableUpload(meta.signedUrl, file, { contentType, onProgress, signal });
    }

    // Legacy single-PUT path ("simple" / undefined) — unchanged behavior.
    return axios.put(meta.signedUrl, file, {
        headers: { "Content-Type": contentType },
        signal,
        onUploadProgress: (e) => {
            if (onProgress) onProgress(e.loaded, e.total || file.size);
        },
    });
}
